// src/reports/GiftCardAnalyticsReport.jsx — Reporting → Store Reports → Backend Reporting → Gift Card Analytics
// (Eric, 2026-10-18). Replaces the Grafana "Lightspeed Gift Card Analytics Dashboard", from OpsHub's Lightspeed tables.
// A redemption is a gift card (or store credit) used to pay on a sale. Needs the Extra Permission reports.backend.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, BlockStack, InlineStack, InlineGrid, Text, Banner, Button, Tabs, Spinner, Box, Tooltip, Select } from '@shopify/polaris';
import { LineChart } from '@shopify/polaris-viz';
import { useSearchParams } from 'react-router-dom';
import { useAuthFetch } from '../AuthComponent';
import { withColors, seriesColor } from './chartPalette';
import ColorDonut from './ColorDonut';
import ReportGuide from './ReportGuide';
import { ReportDateRange, ReportPicker, todayIso, downloadCsv, paneHeightToFitScroller } from '../Reporting';

const fmtN = n => (n === null || n === undefined) ? '—' : Number(n).toLocaleString();
const fmtUSD = n => (n === null || n === undefined) ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const B = ({ children }) => <b>{children}</b>;

const KINDS = [
    { value: 'gift', label: 'Gift cards (store-sold + Team Portal)' },
    { value: 'store', label: 'Store-sold gift cards only' },
    { value: 'team', label: 'Team Portal cards only' },
    { value: 'credit', label: 'Store credit only' },
    { value: 'all', label: 'Everything (gift cards + store credit)' },
];
const TABS = [
    ['stores', 'By store'], ['customers', 'Top customers'], ['employees', 'Top employees'], ['owners', 'Owners vs users'],
    ['detail', 'Redemption details'], ['sold', 'Gift cards sold'],
    ['daily', 'Daily redemptions'], ['dailyValue', 'Daily redemption value'], ['share', 'Redemptions by store'], ['types', 'By card type'],
];
const GUIDE = {
    summary: 'How gift cards (and store credit) are being spent in the stores: how often, how much, where, by whom, and who is using whose card.',
    sections: [
        { heading: 'What it is for', items: [
            'See how many gift card redemptions happen, their dollar value and average size, by store, employee and customer.',
            'See whether cards are being used by the person they were issued to (self-use) or by someone else (gift-use).',
            'See each redemption with the card\'s balance before and after, and how many gift cards the stores sold in the same dates.',
        ] },
        { heading: 'Where the numbers come from', items: [
            "OpsHub's own copy of Lightspeed (our point-of-sale system): sales, payments, and the gift card accounts with their balances.",
            'Refreshed every night (1:15 AM), so the report is complete through last night, not today.',
            'Dates are the store\'s own local date.',
        ] },
        { heading: 'Key terms', items: [
            <><B>Redemption</B>: a gift card (or store credit) used to pay on a sale. One card used on several sales is several redemptions.</>,
            <><B>Redemption value</B>: the dollars paid with cards. <B>Average</B>: value divided by redemptions.</>,
            <><B>Unique customers</B>: different named customers on the sales. Sales with no customer, or the shared "UNKNOWN" customer, count as "Unknown Customer" and are not counted as people.</>,
            <><B>Gift cards sold</B>: money loaded onto a gift card at the register in the same dates and stores. It is not a redemption and not sales revenue.</>,
        ] },
        { heading: 'Types of card (the Show selector)', items: [
            <><B>Store-sold gift cards</B>: cards customers buy at the stores.</>,
            <><B>Team Portal cards</B>: cards the Team Portal issues in bulk (for example "2026 Team Portal Cards", $10 each; 115,000 were loaded in December 2025). They are not sold at stores and are kept out of "Gift cards sold".</>,
            <><B>Store credit</B>: credit accounts that are not gift cards (for example credit from refunds). Store credit is about a quarter of the dollars this year, so it is kept separate.</>,
            'The default shows gift cards (store-sold plus Team Portal). Pick another option to look at one kind.',
        ] },
        { heading: 'Owners vs users', items: [
            '"Owner" is the customer the card was issued to (when Lightspeed knows one); "used by" is the customer on the sale. The same name is Self-use; different names are Gift-use; if either is unknown it is Unknown.',
            'Many cards have no recorded owner, so Unknown is common.',
        ] },
        { heading: 'How to use it', items: [
            'Pick dates (it starts at the beginning of this year), stores (blank = all) and the type of card, then press Run Report. The address in your browser holds your choices.',
            'Tabs show the tables and charts: by store, top customers, top employees, owners vs users, every redemption with balances, gift cards sold, and daily trends.',
            'Click a column title to sort; hover for its meaning; Export CSV saves the table you are viewing.',
        ] },
    ],
};
const COL_HELP = {
    redemptions: 'Times a card was used to pay on a sale.',
    value: 'Dollars paid with cards.',
    total: 'Dollars paid with cards.',
    avg: 'Dollars ÷ redemptions.',
    customers: 'Different named customers on the sales.',
    cards_used: 'Different gift cards used.',
    stores_used: 'Different stores the customer used cards at.',
    usage: 'Self-use: the card owner is the customer on the sale. Gift-use: a different person. Unknown: the owner or the customer is not known.',
    prior_balance: "The card's balance before this redemption.",
    current_balance: "The card's balance after this redemption.",
    card_status: 'Active, depleted (balance zero), archived (spent or closed in Lightspeed) or unknown.',
};

function SortTable({ cols, rows, note = null, fit = true }) {
    const [sort, setSort] = useState({ key: null, dir: 'desc' });
    const wrapRef = useRef(null);
    const [paneH, setPaneH] = useState(null);
    useLayoutEffect(() => {
        if (!fit) return undefined;
        const measure = () => { const el = wrapRef.current; if (!el) return; const h = paneHeightToFitScroller(el); setPaneH(prev => (prev != null && Math.abs(prev - h) <= 1) ? prev : h); };
        measure();
        const t = setTimeout(measure, 300);
        window.addEventListener('resize', measure);
        return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
    }, [fit, rows, paneH]);
    const sorted = useMemo(() => {
        if (!sort.key) return rows;
        const k = sort.key, d = sort.dir === 'asc' ? 1 : -1;
        return [...rows].sort((a, b) => {
            const x = a[k], y = b[k];
            if (x === y) return 0;
            if (x === null || x === undefined || x === '') return 1;
            if (y === null || y === undefined || y === '') return -1;
            return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y))) * d;
        });
    }, [rows, sort]);
    const cell = (r, k, kind) => {
        const v = r[k];
        if (kind === 'n') return fmtN(v);
        if (kind === 'u') return fmtUSD(v);
        if (kind === 'l') return v ? <a href={v} target="_blank" rel="noreferrer">View in Lightspeed</a> : '';
        return v ?? '';
    };
    const left = kind => ['s', 'l', 'w'].includes(kind);
    return (
        <BlockStack gap="200">
            {note && <Text as="p" variant="bodySm" tone="subdued">{note}</Text>}
            <div ref={wrapRef} style={{ overflow: 'auto', maxHeight: fit ? (paneH || undefined) : 460 }}>
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'var(--p-color-bg-surface, #fff)', zIndex: 1 }}>
                        <tr>{cols.map(([k, label, kind, help]) => (
                            <th key={k} onClick={() => setSort(s => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
                                style={{ textAlign: left(kind) ? 'left' : 'right', padding: '6px 8px', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: '1px solid var(--p-color-border, #ddd)' }}>
                                <Tooltip content={help || COL_HELP[k] || label}><span style={{ borderBottom: '1px dotted currentColor' }}>{label}</span></Tooltip>{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>))}</tr>
                    </thead>
                    <tbody>{sorted.map((r, i) => (
                        <tr key={i}>{cols.map(([k, , kind]) => (
                            <td key={k} title={kind === 'w' ? String(r[k] ?? '') : undefined}
                                style={{ textAlign: left(kind) ? 'left' : 'right', padding: '5px 8px', borderBottom: '1px solid var(--p-color-border-subdued, #eee)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', maxWidth: kind === 'w' ? 320 : undefined, overflow: kind === 'w' ? 'hidden' : undefined, textOverflow: kind === 'w' ? 'ellipsis' : undefined }}>
                                {cell(r, k, kind)}</td>))}</tr>))}
                    </tbody>
                </table>
                {rows.length === 0 && <Box padding="400"><Text as="p" tone="subdued" alignment="center">Nothing in these dates.</Text></Box>}
            </div>
        </BlockStack>
    );
}

function Stat({ label, value, sub, hint }) {
    const body = (
        <Box padding="400" background="bg-surface-secondary" borderRadius="200">
            <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">{label}</Text>
                <Text as="p" variant="heading2xl">{value}</Text>
                {sub && <Text as="p" variant="bodySm" tone="subdued">{sub}</Text>}
            </BlockStack>
        </Box>);
    return hint ? <Tooltip content={hint}>{body}</Tooltip> : body;
}

function lines(daily, dates, key) {
    const by = {}, tot = {};
    daily.forEach(r => { (by[r.store] = by[r.store] || {})[r.date] = r[key]; tot[r.store] = (tot[r.store] || 0) + (r.value || 0); });
    return withColors(Object.keys(by).sort((a, b) => tot[b] - tot[a]).map(st => ({
        name: st, data: dates.map(d => ({ key: d, value: by[st][d] !== undefined ? Math.round(by[st][d] * 100) / 100 : 0 })),
    })));
}

export default function GiftCardAnalyticsReport() {
    const fetchAuth = useAuthFetch();
    const [sp, setSp] = useSearchParams();
    const yearStart = `${new Date().getFullYear()}-01-01`;
    const [dateFrom, setDateFrom] = useState(sp.get('from') || yearStart);
    const [dateTo, setDateTo] = useState(sp.get('to') || todayIso());
    const [stores, setStores] = useState(sp.getAll('stores'));
    const [kind, setKind] = useState(sp.get('kind') || 'gift');
    const [tab, setTabState] = useState(Math.max(0, TABS.findIndex(t => t[0] === sp.get('tab'))));
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        document.body.classList.add('report-flush-bottom');
        return () => document.body.classList.remove('report-flush-bottom');
    }, []);

    const writeUrl = useCallback((f, tabIdx) => {
        setSp(prev => {
            const n = new URLSearchParams(prev);
            ['from', 'to', 'stores', 'kind', 'tab'].forEach(k => n.delete(k));
            n.set('from', f.dateFrom); n.set('to', f.dateTo);
            f.stores.forEach(v => n.append('stores', v));
            if (f.kind !== 'gift') n.set('kind', f.kind);
            if (tabIdx > 0) n.set('tab', TABS[tabIdx][0]);
            return n;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSp]);
    const setTab = (i) => { setTabState(i); writeUrl({ dateFrom, dateTo, stores, kind }, i); };

    const run = useCallback(async () => {
        setBusy(true); setErr(null);
        writeUrl({ dateFrom, dateTo, stores, kind }, tab);
        try {
            const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, kind });
            stores.forEach(v => p.append('stores', v));
            const r = await fetchAuth(`/api/reports/backend/gift-card-analytics?${p}`);
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
            setData(await r.json());
        } catch (e) { setErr(String(e.message || e)); } finally { setBusy(false); }
    }, [fetchAuth, dateFrom, dateTo, stores, kind, tab, writeUrl]);
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    const storeOptions = useMemo(() => (data?.store_options || []).map(v => ({ value: v, label: v })), [data]);
    const dates = useMemo(() => {
        if (!data) return [];
        const out = [];
        for (let d = new Date(`${data.date_from}T00:00:00Z`); d <= new Date(`${data.date_to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) out.push(d.toISOString().slice(0, 10));
        return out;
    }, [data]);
    const series = useMemo(() => ({ count: lines(data?.daily || [], dates, 'redemptions'), value: lines(data?.daily || [], dates, 'value') }), [data, dates]);
    const donutStores = useMemo(() => (data?.by_store || []).map((r, i) => ({ name: r.store, color: seriesColor(i), data: [{ key: 'n', value: r.redemptions }] })), [data]);
    const donutTypes = useMemo(() => (data?.by_type || []).map((r, i) => ({ name: r.type, color: seriesColor(i), data: [{ key: 'v', value: r.value }] })), [data]);

    const S = data?.summary, L = data?.loads_summary;
    const key = TABS[tab][0];
    const COLS = {
        stores: [['store', 'Store', 's'], ['redemptions', 'Redemptions', 'n'], ['value', 'Redemption value', 'u'], ['avg', 'Avg redemption', 'u'], ['customers', 'Unique customers', 'n'], ['cards_used', 'Cards used', 'n']],
        customers: [['customer', 'Customer', 's'], ['redemptions', 'Redemption count', 'n'], ['total', 'Total amount', 'u'], ['avg', 'Avg amount', 'u'], ['stores_used', 'Stores used', 'n'], ['cards_used', 'Cards used', 'n']],
        employees: [['employee', 'Employee', 's'], ['store', 'Store', 's'], ['redemptions', 'Redemptions processed', 'n'], ['total', 'Total amount', 'u'], ['avg', 'Avg redemption', 'u']],
        owners: [['owner', 'Gift card owner', 's'], ['used_by', 'Used by customer', 's'], ['cards_used', 'Gift cards used', 'n'], ['redemptions', 'Total redemptions', 'n'], ['value', 'Total value', 'u'], ['usage', 'Usage type', 's']],
        detail: [['date', 'Date', 's'], ['time', 'Time', 's'], ['store', 'Store', 's'], ['employee', 'Employee', 's'], ['customer', 'Customer', 's'], ['card_type', 'Card type', 's'],
            ['card_name', 'Card name', 'w'], ['card_status', 'Card status', 's'], ['prior_balance', 'Balance before', 'u'], ['amount', 'Redeemed', 'u'], ['current_balance', 'Balance after', 'u'], ['link', 'View in Lightspeed', 'l']],
        sold: [['date', 'Date', 's'], ['store', 'Store', 's'], ['employee', 'Employee', 's'], ['card_name', 'Card name', 'w'], ['amount', 'Amount loaded', 'u'], ['link', 'View in Lightspeed', 'l']],
    };
    const rowsFor = { stores: data?.by_store, customers: data?.customers, employees: data?.employees, owners: data?.owners, detail: data?.detail, sold: data?.loads };
    const exportCsv = () => {
        if (!data || !COLS[key]) return;
        downloadCsv(`gift-card-analytics-${key}-${dateFrom}-${dateTo}.csv`, [COLS[key].map(c => c[1]), ...(rowsFor[key] || []).map(r => COLS[key].map(c => r[c[0]] ?? ''))]);
    };
    const moneyAxis = { yAxisOptions: { labelFormatter: v => '$' + Number(v).toLocaleString() }, tooltipOptions: { valueFormatter: fmtUSD } };

    return (
        <BlockStack gap="300">
            {err && <Banner tone="critical" onDismiss={() => setErr(null)}>{err}</Banner>}
            <ReportGuide id="gift-card-analytics" summary={GUIDE.summary} sections={GUIDE.sections} />
            <Card>
                <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center" wrap>
                        <ReportDateRange dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
                        <ReportPicker label="Store" options={storeOptions} value={stores} onChange={setStores} multiple searchable placeholder="All stores" />
                        <div style={{ minWidth: 330 }}><Select label="Show" labelInline options={KINDS} value={kind} onChange={setKind} /></div>
                        <Button variant="primary" onClick={run} loading={busy}>Run Report</Button>
                        <Button onClick={exportCsv} disabled={!data || !COLS[key]}>Export CSV</Button>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                        A redemption is a gift card or store credit used to pay on a sale, by the store's local date.
                        {data?.synced_through ? ` Sales load nightly, through ${String(data.synced_through).slice(0, 16).replace('T', ' ')}.` : ''}
                    </Text>
                </BlockStack>
            </Card>

            {busy && !data && <InlineStack align="center"><Spinner /></InlineStack>}
            {S && (
                <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="200">
                    <Stat label="Total redemptions" value={fmtN(S.redemptions)} hint="Times a card was used to pay on a sale." />
                    <Stat label="Total redemption value" value={fmtUSD(S.value)} hint="Dollars paid with cards." />
                    <Stat label="Average redemption amount" value={fmtUSD(S.avg)} hint="Redemption value ÷ redemptions." />
                    <Stat label="Unique customers" value={fmtN(S.customers)} hint="Different named customers on the sales; unknown customers are not counted." />
                    <Stat label="Different cards used" value={fmtN(S.cards_used)} hint="Gift cards or credit accounts that paid for at least one sale." />
                    <Stat label="Gift cards sold at stores" value={fmtUSD(L?.sold_value)} sub={`${fmtN(L?.sold_cards)} cards loaded`} hint="Money loaded onto gift cards at the registers in these dates and stores. Not a redemption and not sales revenue. Team Portal bulk-issued cards are not included." />
                </InlineGrid>
            )}

            {data && (
                <Card padding="0">
                    <Tabs tabs={TABS.map(([id, content]) => ({ id, content }))} selected={tab} onSelect={setTab} />
                    <Box padding="300" paddingBlockEnd="0">
                        {COLS[key] && (
                            <SortTable key={key} cols={COLS[key]} rows={rowsFor[key] || []}
                                note={(key === 'detail' || key === 'sold') && (rowsFor[key] || []).length >= data.detail_limit ? `Showing the latest ${fmtN(data.detail_limit)} — narrow the dates or filters.` : null} />
                        )}
                        {key === 'daily' && <div style={{ height: 420 }}><LineChart data={series.count} /></div>}
                        {key === 'dailyValue' && <div style={{ height: 420 }}><LineChart data={series.value} {...moneyAxis} /></div>}
                        {key === 'share' && <ColorDonut data={donutStores} format={fmtN} height={420} />}
                        {key === 'types' && <ColorDonut data={donutTypes} format={fmtUSD} height={300} />}
                    </Box>
                </Card>
            )}
        </BlockStack>
    );
}
